import { clamp01 } from '../core/math';
import type { BusName } from './audioService';
import { BUS_LEVELS, environmentTone, softClipCurve, type Environment } from './mix';

/**
 * Web Audio mixer: lazily-created AudioContext, bus graph, shared noise buffers, ducking, voice budget.
 * Nothing here touches window/AudioContext at import time (vitest runs in node).
 *
 * Graph (levels: BUS_LEVELS in mix.ts; environment street / car / interior: `setEnvironment`):
 *   siren, others' engines --> vehicleDuck --> vehicle env (lowpass + gain) --+
 *   player's engine --> ownDuck ---------------------------------------------+
 *   ambience --> ambienceDuck --> ambience env (lowpass + gain) -------------+--> worldGain (pause mute) --+
 *   sfx, weapons, voice -----------------------------------------------------^                            |
 *   echo (send) --> street slap-back delays ----------------------------------^                            |
 *   ui ------------------------------------------------------------------------------> sfxGroup (volume 'sfx') <--+
 *   music (radio) --> music muffle (lowpass + gain: a radio through a wall, `setMusicMuffle`) --> musicDuck --> actionDuck --> sirenDuck --> musicGroup (volume 'music')
 *   sfxGroup + musicGroup --> master --> glue comp --> limiter --> soft clipper --> fade --> out
 *
 * Mobile: the context is created/resumed from a gesture (incl. touchend/click for iOS, plus one silent
 * sample to finish the unlock), suspended while the page is hidden and resumed when it returns; an iOS
 * 'interrupted' context (call, Siri, backgrounding) is resumed on the next gesture.
 */

/** Radio duck under the pause menu (gain 1 - 0.68 = 0.32, about -10 dB). */
export const PAUSE_MUSIC_DUCK = 0.68;

export const DEFAULT_VOLUMES: Record<BusName, number> = { master: 0.8, music: 0.6, sfx: 0.9 };

/** Perceptual taper from slider value (0..1) to linear gain. */
export function volumeGain(v: number): number {
  return Math.pow(clamp01(v), 1.5);
}

/** Final linear gain of a bus given its slider and the master slider. */
export function effectiveGain(master: number, bus: number): number {
  return volumeGain(master) * volumeGain(bus);
}

export interface DuckState {
  amount: number;
  /** Audio time (s) when the duck releases. */
  end: number;
}

/** Merge a new duck request into the current one (overlapping ducks keep the deepest / longest). */
export function mergeDuck(state: DuckState, now: number, amount: number, seconds: number): DuckState {
  const a = clamp01(amount);
  if (now < state.end) {
    state.amount = Math.max(state.amount, a);
    state.end = Math.max(state.end, now + seconds);
  } else {
    state.amount = a;
    state.end = now + seconds;
  }
  return state;
}

export type NoiseKind = 'white' | 'pink' | 'brown';

/** Fill a mono buffer with seamlessly-looping noise (loop seam crossfaded). Pure, unit-testable. */
export function fillNoise(out: Float32Array, kind: NoiseKind, rand: () => number = Math.random): void {
  const len = out.length;
  const seam = Math.min(2048, len >> 2);
  const gen = new Float32Array(len + seam);
  let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0, brown = 0;
  for (let i = 0; i < gen.length; i++) {
    const w = rand() * 2 - 1;
    if (kind === 'white') {
      gen[i] = w;
    } else if (kind === 'pink') {
      // Paul Kellet's economy pink filter.
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      gen[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362) * 0.11;
      b6 = w * 0.115926;
    } else {
      brown = (brown + 0.02 * w) / 1.02;
      gen[i] = brown * 3.5;
    }
  }
  for (let i = 0; i < len; i++) out[i] = gen[i] as number;
  for (let i = 0; i < seam; i++) {
    const t = i / seam;
    out[i] = (gen[i] as number) * t + (gen[len + i] as number) * (1 - t);
  }
}

/** One ducking channel: a gain node dipped by `trigger` and released smoothly. */
class DuckChannel {
  readonly node: GainNode;
  private state: DuckState = { amount: 0, end: 0 };
  constructor(ctx: AudioContext) {
    this.node = ctx.createGain();
  }
  trigger(now: number, amount: number, seconds: number): void {
    const s = mergeDuck(this.state, now, amount, Math.max(0.05, seconds));
    const p = this.node.gain;
    p.cancelScheduledValues(now);
    p.setTargetAtTime(1 - s.amount, now, 0.04);
    p.setTargetAtTime(1, s.end, 0.25);
  }
}

export interface Buses {
  /** Other cars' engines (muffled indoors). */
  engine: GainNode;
  /** The player's own car (never muffled: you're in it). */
  ownEngine: GainNode;
  sfx: GainNode;
  /** Gunshots, reloads, swings, blows. */
  weapons: GainNode;
  /** Grunts, screams, whistles. */
  voice: GainNode;
  ui: GainNode;
  ambience: GainNode;
  music: GainNode;
  siren: GainNode;
  /** Send into the street slap-back (feed it a copy of a shot / boom / horn). */
  echo: GainNode;
}

type CtxCtor = typeof AudioContext;

function ctxCtor(): CtxCtor | null {
  if (typeof globalThis === 'undefined') return null;
  const g = globalThis as unknown as { AudioContext?: CtxCtor; webkitAudioContext?: CtxCtor };
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

/** Max simultaneous non-essential one-shot voices; further ones are dropped. */
export const MAX_ONESHOT_VOICES = 40;

export class Mixer {
  /** True when this environment has an AudioContext constructor. */
  readonly available: boolean;
  ctx: AudioContext | null = null;
  buses!: Buses;
  noise!: Record<NoiseKind, AudioBuffer>;
  /** Band-limited sawtooth (12 harmonics) for siren / brass-ish voices. */
  sawLimited!: PeriodicWave;
  /** Live one-shot voices (oscillators/noise sources not yet ended). */
  voiceCount = 0;
  /** One-shot voice cap (`voiceBudget` by render tier: fewer on LOW). */
  maxVoices = MAX_ONESHOT_VOICES;
  /** Where the listener is (ambience / other cars muffled in the car and indoors). */
  environment: Environment = 'street';
  readonly volumes: Record<BusName, number> = { ...DEFAULT_VOLUMES };
  /** Set by `attach` (offline rendering): the graph is scheduled while the context is still suspended. */
  private assumeRunning = false;
  private readonly waves = new Map<string, PeriodicWave>();

  private master!: GainNode;
  private sfxGroup!: GainNode;
  private worldGain!: GainNode;
  private musicGroup!: GainNode;
  private ambienceDuck!: DuckChannel;
  private musicDuck!: DuckChannel;
  private vehicleDuck!: DuckChannel;
  private readyCbs: (() => void)[] = [];
  private listenersAttached = false;
  private readonly gestureEvents = ['pointerdown', 'pointerup', 'keydown', 'touchstart', 'touchend', 'mousedown', 'click'] as const;
  private readonly onGesture = (): void => {
    if (!this.ctx || this.ctx.state !== 'running') this.unlock();
  };
  private lifecycleAttached = false;
  /** True while the page is hidden (we suspended the context ourselves). */
  private hidden = false;
  private readonly onVisibility = (): void => {
    this.setHidden(typeof document !== 'undefined' && document.visibilityState === 'hidden');
  };
  private readonly onPageHide = (): void => this.setHidden(true);
  private readonly onPageShow = (): void => this.setHidden(false);
  private actionDuck!: DuckChannel;
  private limiter: DynamicsCompressorNode | null = null;
  private ambEnvLp!: BiquadFilterNode;
  private ambEnvGain!: GainNode;
  private vehEnvLp!: BiquadFilterNode;
  private vehEnvGain!: GainNode;
  private ownDuck!: GainNode;
  /** Continuous radio dip under the nearest siren (`setSirenDuck`); the ambience share rides the env gain. */
  private musicSiren!: GainNode;
  private sirenAmb = 0;
  /** The radio heard through a wall (the lock-up on the profile page): a lowpass and a dip, open by default. */
  private musicMuffle!: BiquadFilterNode;
  private musicMuffleGain!: GainNode;
  /** Output fade around suspend / resume (no click going to the background, no burst coming back). */
  private fade!: GainNode;
  /** The context stopped after it had been running (background, call, Siri): fade in on the next resume. */
  private resumeSoft = false;

  constructor() {
    this.available = ctxCtor() !== null;
  }

  /** Graph built (context exists). Does not imply running. */
  get ready(): boolean {
    return this.ctx !== null;
  }
  /** Context exists and is running: safe to schedule sound. */
  get running(): boolean {
    return this.ctx !== null && (this.assumeRunning || this.ctx.state === 'running');
  }

  /**
   * Build the graph on a context supplied by the caller (an OfflineAudioContext in the audio lab and
   * tests). The mixer then reports `running` whatever the context's state, so systems schedule into it
   * while it is still suspended ahead of `startRendering()`.
   */
  attach(ctx: BaseAudioContext): void {
    if (this.ctx) return;
    this.ctx = ctx as AudioContext;
    this.assumeRunning = true;
    this.build(this.ctx);
    for (const cb of this.readyCbs.splice(0)) cb();
  }

  /** A cached PeriodicWave (built once per context) for instruments with fixed harmonic recipes. */
  periodicWave(key: string, build: () => { real: Float32Array; imag: Float32Array }): PeriodicWave {
    let w = this.waves.get(key);
    if (!w) {
      const { real, imag } = build();
      w = (this.ctx as AudioContext).createPeriodicWave(real as Float32Array<ArrayBuffer>, imag as Float32Array<ArrayBuffer>);
      this.waves.set(key, w);
    }
    return w;
  }
  get contextState(): string {
    return this.ctx ? this.ctx.state : this.available ? 'none' : 'unavailable';
  }
  now(): number {
    return this.ctx ? this.ctx.currentTime : 0;
  }

  /** Run `cb` once the context + graph exist (immediately if already built). */
  whenReady(cb: () => void): void {
    if (this.ctx) cb();
    else this.readyCbs.push(cb);
  }

  /** Auto-unlock on the first user gesture (pointer/key/touch), and suspend/resume with page visibility. */
  attachGestureListeners(): void {
    if (!this.available || typeof window === 'undefined') return;
    if (!this.listenersAttached) {
      this.listenersAttached = true;
      for (const e of this.gestureEvents) window.addEventListener(e, this.onGesture, { capture: true, passive: true });
    }
    if (!this.lifecycleAttached) {
      this.lifecycleAttached = true;
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', this.onPageShow);
    }
  }

  /** Page hidden (app backgrounded, tab switched): suspend. Shown again: resume (or wait for a gesture). */
  setHidden(hide: boolean): void {
    if (hide === this.hidden) return;
    this.hidden = hide;
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      if (hide) {
        if (ctx.state === 'running') {
          // Fade out over ~40 ms before suspending: a suspend mid-waveform clicks on iPhone speakers.
          this.fade.gain.setTargetAtTime(0, ctx.currentTime, 0.012);
          setTimeout(() => {
            if (this.hidden && this.ctx === ctx && ctx.state === 'running') void ctx.suspend().catch(() => undefined);
          }, 60);
        }
      } else {
        void ctx.resume().then(
          () => this.fadeIn(),
          () => undefined,
        );
        // iOS may refuse a resume without a gesture: keep listening for one.
        this.attachGestureListeners();
      }
    } catch {
      /* closed */
    }
  }

  /** Bring the output back up softly after a resume, so whatever was scheduled meanwhile doesn't burst in. */
  private fadeIn(): void {
    const ctx = this.ctx;
    if (!ctx || !this.fade || this.hidden) return;
    const g = this.fade.gain;
    // The very first unlock plays at once (the tap's own click); only a comeback fades in.
    if (!this.resumeSoft && g.value > 0.99) return;
    this.resumeSoft = false;
    g.cancelScheduledValues(ctx.currentTime);
    g.setValueAtTime(0.0001, ctx.currentTime);
    g.setTargetAtTime(1, ctx.currentTime, 0.12);
  }

  /** iOS only finishes the unlock once something plays inside the gesture: one silent sample. */
  private primeSilence(ctx: AudioContext): void {
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      src.connect(ctx.destination);
      src.onended = () => src.disconnect();
      src.start(0);
    } catch {
      /* not fatal */
    }
  }

  private detachGestureListeners(): void {
    if (!this.listenersAttached || typeof window === 'undefined') return;
    this.listenersAttached = false;
    for (const e of this.gestureEvents) window.removeEventListener(e, this.onGesture, { capture: true });
  }

  /** Create (first call) and resume the context. Safe to call repeatedly / without gesture. */
  unlock(): void {
    const Ctor = ctxCtor();
    if (!Ctor) return;
    try {
      if (!this.ctx) {
        this.ctx = new Ctor({ latencyHint: 'interactive' });
        this.build(this.ctx);
        this.ctx.onstatechange = () => {
          const st = this.ctx ? (this.ctx.state as string) : 'closed';
          if (st !== 'running' && st !== 'closed') this.resumeSoft = true;
          if (st === 'running') this.detachGestureListeners();
          // iOS 'interrupted' (a call, Siri) or a suspend we did not ask for: resume on the next gesture.
          else if (st !== 'closed' && !this.hidden) this.attachGestureListeners();
        };
        const cbs = this.readyCbs.splice(0);
        for (const cb of cbs) {
          try {
            cb();
          } catch (e) {
            console.warn('[audio] ready callback failed', e);
          }
        }
      }
      if (this.ctx.state !== 'running' && !this.hidden) {
        this.primeSilence(this.ctx);
        void this.ctx.resume().then(
          () => {
            if (this.ctx && this.ctx.state === 'running') {
              this.detachGestureListeners();
              this.fadeIn();
            }
          },
          () => undefined,
        );
      } else {
        this.detachGestureListeners();
      }
    } catch (e) {
      console.warn('[audio] unlock failed', e);
    }
  }

  private build(ctx: AudioContext): void {
    this.master = ctx.createGain();
    // Gentle glue compressor (a wide knee, slow enough to leave transients alone, 2:1), then a fast
    // limiter so stacked explosions / gunshots never clip.
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 20;
    comp.ratio.value = 2;
    comp.attack.value = 0.015;
    comp.release.value = 0.3;
    const lim = ctx.createDynamicsCompressor();
    lim.threshold.value = -2.5;
    lim.knee.value = 0;
    lim.ratio.value = 20;
    lim.attack.value = 0.0015;
    lim.release.value = 0.12;
    this.limiter = lim;
    // The DynamicsCompressor limiter is no brickwall (its attack lets the first ms of a stacked hit
    // through): a soft clipper, transparent below -1 dBFS, rounds what is left instead of hard-clipping.
    const clip = ctx.createWaveShaper();
    clip.curve = softClipCurve() as Float32Array<ArrayBuffer>;
    this.fade = ctx.createGain();
    this.master.connect(comp).connect(lim).connect(clip).connect(this.fade).connect(ctx.destination);

    this.sfxGroup = ctx.createGain();
    this.musicGroup = ctx.createGain();
    this.sfxGroup.connect(this.master);
    this.musicGroup.connect(this.master);

    this.ambienceDuck = new DuckChannel(ctx);
    this.musicDuck = new DuckChannel(ctx);
    this.vehicleDuck = new DuckChannel(ctx);
    this.actionDuck = new DuckChannel(ctx);
    this.worldGain = ctx.createGain();
    this.worldGain.connect(this.sfxGroup);
    const envChain = (): [BiquadFilterNode, GainNode] => {
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 18000;
      lp.Q.value = 0.5;
      const g = ctx.createGain();
      lp.connect(g).connect(this.worldGain);
      return [lp, g];
    };
    [this.ambEnvLp, this.ambEnvGain] = envChain();
    [this.vehEnvLp, this.vehEnvGain] = envChain();
    this.ambienceDuck.node.connect(this.ambEnvLp);
    this.vehicleDuck.node.connect(this.vehEnvLp);
    // The player's own engine dips under explosions like the rest but is never muffled by a room.
    this.ownDuck = ctx.createGain();
    this.ownDuck.connect(this.worldGain);
    this.musicSiren = ctx.createGain();
    this.musicDuck.node.connect(this.actionDuck.node).connect(this.musicSiren).connect(this.musicGroup);
    this.musicMuffle = ctx.createBiquadFilter();
    this.musicMuffle.type = 'lowpass';
    this.musicMuffle.frequency.value = 20000;
    this.musicMuffle.Q.value = 0.3;
    this.musicMuffleGain = ctx.createGain();
    this.musicMuffle.connect(this.musicMuffleGain).connect(this.musicDuck.node);

    // Street slap-back: two short, darkened, cross-fed delays (brick terraces either side). Gunshots,
    // explosions and the horn send a copy into it.
    const echoIn = ctx.createGain();
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 180;
    const dark = ctx.createBiquadFilter();
    dark.type = 'lowpass';
    dark.frequency.value = 2400;
    dark.Q.value = 0.5;
    const dA = ctx.createDelay(1);
    dA.delayTime.value = 0.13;
    const dB = ctx.createDelay(1);
    dB.delayTime.value = 0.21;
    const fbA = ctx.createGain();
    fbA.gain.value = 0.3;
    const fbB = ctx.createGain();
    fbB.gain.value = 0.26;
    const panA = ctx.createStereoPanner();
    panA.pan.value = -0.6;
    const panB = ctx.createStereoPanner();
    panB.pan.value = 0.6;
    const echoOut = ctx.createGain();
    echoOut.gain.value = 0.5;
    echoIn.connect(hp).connect(dark);
    dark.connect(dA);
    dark.connect(dB);
    dA.connect(fbA).connect(dB);
    dB.connect(fbB).connect(dA);
    dA.connect(panA).connect(echoOut);
    dB.connect(panB).connect(echoOut);
    echoOut.connect(this.worldGain);

    const bus = (dest: AudioNode, level: number): GainNode => {
      const g = ctx.createGain();
      g.gain.value = level;
      g.connect(dest);
      return g;
    };
    const L = BUS_LEVELS;
    this.buses = {
      engine: bus(this.vehicleDuck.node, L.engine),
      ownEngine: bus(this.ownDuck, L.engine),
      siren: bus(this.vehicleDuck.node, L.siren),
      sfx: bus(this.worldGain, L.sfx),
      weapons: bus(this.worldGain, L.weapons),
      voice: bus(this.worldGain, L.voice),
      ui: bus(this.sfxGroup, L.ui),
      ambience: bus(this.ambienceDuck.node, L.ambience),
      music: bus(this.musicMuffle, L.music),
      echo: echoIn,
    };
    this.applyEnvironment(true);

    const len = Math.floor(ctx.sampleRate * 3);
    const mk = (kind: NoiseKind): AudioBuffer => {
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      fillNoise(buf.getChannelData(0), kind);
      return buf;
    };
    this.noise = { white: mk('white'), pink: mk('pink'), brown: mk('brown') };

    const n = 12;
    const real = new Float32Array(n + 1);
    const imag = new Float32Array(n + 1);
    for (let k = 1; k <= n; k++) imag[k] = 1 / k;
    this.sawLimited = ctx.createPeriodicWave(real, imag);

    this.applyVolumes(true);
  }

  private applyVolumes(immediate = false): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const set = (p: AudioParam, v: number): void => {
      if (immediate) p.setValueAtTime(v, t);
      else p.setTargetAtTime(v, t, 0.04);
    };
    set(this.master.gain, volumeGain(this.volumes.master));
    set(this.sfxGroup.gain, volumeGain(this.volumes.sfx));
    set(this.musicGroup.gain, volumeGain(this.volumes.music));
  }

  setVolume(bus: BusName, v: number): void {
    this.volumes[bus] = clamp01(Number.isFinite(v) ? v : 0);
    this.applyVolumes();
  }

  /** Duck ambience + radio. */
  duck(amount: number, seconds: number): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.ambienceDuck.trigger(t, amount, seconds);
    this.musicDuck.trigger(t, amount, seconds);
  }

  /**
   * Mute/unmute world sound (engines, sirens, ambience, impacts) while paused. UI is unaffected and the
   * radio keeps playing under the menu, ducked by `PAUSE_MUSIC_DUCK` (about -10 dB). Called every frame.
   */
  setPaused(paused: boolean): void {
    if (!this.ctx) return;
    this.worldGain.gain.setTargetAtTime(paused ? 0 : 1, this.ctx.currentTime, 0.06);
    if (paused) this.musicDuck.trigger(this.ctx.currentTime, PAUSE_MUSIC_DUCK, 0.3);
  }

  /** Duck the radio only (action: pursuits, gunfights), leaving the ambience alone. */
  duckMusic(amount: number, seconds: number): void {
    if (!this.ctx) return;
    this.actionDuck.trigger(this.ctx.currentTime, amount, seconds);
  }

  /** Limiter gain reduction in dB (<= 0), for diagnostics. */
  get limiterReduction(): number {
    return this.limiter ? this.limiter.reduction : 0;
  }

  /** Duck engines + sirens (explosions). */
  duckVehicles(amount: number, seconds: number): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.vehicleDuck.trigger(t, amount, seconds);
    const g = this.ownDuck.gain;
    g.cancelScheduledValues(t);
    g.setTargetAtTime(1 - clamp01(amount), t, 0.04);
    g.setTargetAtTime(1, t + Math.max(0.05, seconds), 0.25);
  }

  /** Duck the ambience only (under a line of dialogue the street sits back a little, the radio more). */
  duckAmbience(amount: number, seconds: number): void {
    if (!this.ctx) return;
    this.ambienceDuck.trigger(this.ctx.currentTime, amount, seconds);
  }

  /**
   * The radio heard through a wall (the lock-up on the profile page): glide its lowpass down to `cutoff` Hz and
   * its level to `level`; null opens it again. Sits before the ducks, so the volume settings apply as ever.
   */
  setMusicMuffle(cutoff: number | null, level = 1): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.musicMuffle.frequency.setTargetAtTime(cutoff ?? 20000, t, 0.35);
    this.musicMuffleGain.gain.setTargetAtTime(cutoff === null ? 1 : clamp01(level), t, 0.35);
  }

  /** Street / car / interior: glides the ambience (and indoors the other cars) through its lowpass. */
  setEnvironment(env: Environment): void {
    if (env === this.environment) return;
    this.environment = env;
    this.applyEnvironment();
  }

  /**
   * Continuous dip of the radio and ambience under the nearest siren (amounts from `sirenDuck` in
   * mix.ts). Called every frame; the gain glides, so a passing car swells the duck in and out.
   */
  setSirenDuck(music: number, ambience: number): void {
    if (!this.ctx) return;
    this.musicSiren.gain.setTargetAtTime(1 - clamp01(music), this.ctx.currentTime, 0.25);
    if (Math.abs(ambience - this.sirenAmb) > 0.01) {
      this.sirenAmb = clamp01(ambience);
      this.applyEnvironment();
    }
  }

  private applyEnvironment(immediate = false): void {
    if (!this.ctx) return;
    const e = environmentTone(this.environment);
    const t = this.ctx.currentTime;
    const set = (p: AudioParam, v: number): void => {
      if (immediate) p.setValueAtTime(v, t);
      else p.setTargetAtTime(v, t, e.tc);
    };
    set(this.ambEnvGain.gain, e.ambGain * (1 - this.sirenAmb));
    set(this.ambEnvLp.frequency, e.ambCutoff);
    set(this.vehEnvGain.gain, e.vehGain);
    set(this.vehEnvLp.frequency, e.vehCutoff);
  }

  /** Looping noise source (not started). Call `startNoise` to begin at a random offset. */
  noiseSource(kind: NoiseKind): AudioBufferSourceNode {
    const src = (this.ctx as AudioContext).createBufferSource();
    src.buffer = this.noise[kind];
    src.loop = true;
    return src;
  }

  startNoise(src: AudioBufferSourceNode, when: number): void {
    const dur = src.buffer ? src.buffer.duration : 1;
    src.start(when, Math.random() * dur);
  }

  /** Reserve a one-shot voice slot. Non-essential sounds are dropped above the cap. */
  canSpawn(essential = false): boolean {
    return essential || this.voiceCount < this.maxVoices;
  }

  dispose(): void {
    this.detachGestureListeners();
    if (this.lifecycleAttached && typeof window !== 'undefined') {
      this.lifecycleAttached = false;
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('pagehide', this.onPageHide);
      window.removeEventListener('pageshow', this.onPageShow);
    }
    this.readyCbs.length = 0;
    this.waves.clear();
    const ctx = this.ctx;
    this.ctx = null;
    if (ctx && !this.assumeRunning) {
      ctx.onstatechange = null;
      void ctx.close().catch(() => undefined);
    }
  }
}
